import { useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { motion } from "framer-motion";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReservationCheckoutForm from "@/components/reserve/ReservationCheckoutForm";
import { supabase } from "@/integrations/supabase/client";
import {
  durationLabel,
  endsBeforeClosing,
  hasEnoughNotice,
  londonDateLabel,
  londonTimeLabel,
  priceForMinutes,
  slotIsTaken,
  startTimesForDate,
  upcomingDates,
  WESTFIELD_POD_ID,
  WESTFIELD_POD_LABEL,
  type ReservedSlot,
} from "@/lib/reservations";

const stripePromise = loadStripe(
  "pk_live_51Qnm0jDlkio8pUGzsgPanjgIMU5dQSlq8K0AYJc5Bh9tGXxPxpEb2n1C1yl6TSXbuRuo8eB1SpwSrJrZADOBn0Ia00VoQ4xp7U",
);

type Step = "choose" | "pay" | "done";

interface Confirmation {
  date_label: string;
  from_label: string;
  to_label: string;
  duration_label: string;
  amount_label: string;
  access_code: string | null;
}

const ReservePod = () => {
  const dates = useMemo(() => upcomingDates(), []);
  const [dateKey, setDateKey] = useState(dates[0].key);
  const [minutes, setMinutes] = useState(60);
  const [startIso, setStartIso] = useState<string>("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [reserved, setReserved] = useState<ReservedSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [step, setStep] = useState<Step>("choose");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [reservationId, setReservationId] = useState("");
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  useEffect(() => {
    document.title = "Reserve a qwip pod";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingSlots(true);
      const from = new Date();
      const to = new Date(from.getTime() + 21 * 24 * 60 * 60 * 1000);
      const { data, error: rpcError } = await supabase.rpc("get_reserved_slots", {
        _pod_id: WESTFIELD_POD_ID,
        _from: from.toISOString(),
        _to: to.toISOString(),
      });
      if (cancelled) return;
      if (rpcError) {
        setError("Error - we could not load availability. Please contact support.");
      } else {
        setReserved((data ?? []) as ReservedSlot[]);
      }
      setLoadingSlots(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const slots = useMemo(() => {
    return startTimesForDate(dateKey).map((start) => {
      const unavailable =
        !hasEnoughNotice(start) ||
        !endsBeforeClosing(start, minutes, dateKey) ||
        slotIsTaken(start, minutes, reserved);
      return { start, label: londonTimeLabel(start), unavailable };
    });
  }, [dateKey, minutes, reserved]);

  useEffect(() => {
    if (!startIso) return;
    const stillValid = slots.some((s) => s.start.toISOString() === startIso && !s.unavailable);
    if (!stillValid) setStartIso("");
  }, [slots, startIso]);

  const selectedStart = startIso ? new Date(startIso) : null;
  const canContinue =
    !!selectedStart && name.trim().length > 1 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const startReservation = async () => {
    if (!canContinue || !selectedStart) return;
    setSubmitting(true);
    setError("");

    const { data, error: fnError } = await supabase.functions.invoke("create-reservation", {
      body: {
        pod_id: WESTFIELD_POD_ID,
        pod_label: WESTFIELD_POD_LABEL,
        start_at: selectedStart.toISOString(),
        duration_minutes: minutes,
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
      },
    });

    if (fnError || !data?.client_secret) {
      setError(data?.error || "Error - we could not start your reservation. Please contact support.");
      setSubmitting(false);
      return;
    }

    setClientSecret(data.client_secret);
    setReservationId(data.reservation_id);
    setStep("pay");
    setSubmitting(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finishReservation = async () => {
    setSubmitting(true);
    setError("");
    const { data, error: fnError } = await supabase.functions.invoke("confirm-reservation", {
      body: { reservation_id: reservationId, client_secret: clientSecret },
    });

    if (fnError || !data || data.error) {
      setError(
        data?.error ||
          "Your payment went through but we could not confirm the booking. Please contact support.",
      );
      setSubmitting(false);
      return;
    }

    setConfirmation({
      date_label: data.date_label,
      from_label: data.from_label,
      to_label: data.to_label,
      duration_label: data.duration_label,
      amount_label: data.amount_label,
      access_code: data.access_code ?? null,
    });
    setStep("done");
    setSubmitting(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />

      <main className="flex-1 px-4 pb-20 pt-28 tablet:px-6 tablet:pt-32">
        <div className="mx-auto max-w-2xl">
          {step !== "done" && (
            <div className="mb-8 text-center">
              <h1 className="font-funnel text-3xl font-semibold text-primary tablet:text-4xl">
                Reserve a pod in advance
              </h1>
              <p className="mt-3 text-foreground/70">
                Guarantee the pod for a specific time. £15 per hour, from 1 hour, in 15 minute steps.
              </p>
              <p className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground/80" role="status">
                Our pay-as-you-go booking option is currently undergoing an upgrade. You can
                reserve the pod for tomorrow onwards, from the next available start time.
                The pod is unavailable from Friday 23 to Sunday 25 October.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground/70">
                <MapPin className="h-4 w-4 text-primary" />
                {WESTFIELD_POD_LABEL}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          {step === "choose" && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-8 rounded-2xl border border-border bg-card p-6 tablet:p-8"
            >
              <section>
                <h2 className="mb-3 flex items-center gap-2 font-funnel text-lg font-semibold text-primary">
                  <CalendarDays className="h-5 w-5" /> Pick a date
                </h2>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {dates.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setDateKey(d.key)}
                      className={`shrink-0 rounded-xl border px-4 py-3 text-sm transition-colors ${
                        dateKey === d.key
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground/80 hover:bg-muted"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="mb-3 flex items-center gap-2 font-funnel text-lg font-semibold text-primary">
                  <Clock className="h-5 w-5" /> How long for?
                </h2>
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="mb-3 flex items-baseline justify-between">
                    <span className="font-funnel text-xl font-semibold text-primary">
                      {durationLabel(minutes)}
                    </span>
                    <span className="text-sm text-foreground/60">
                      <strong className="text-foreground">{priceForMinutes(minutes)}</strong> total
                    </span>
                  </div>
                  <input
                    type="range"
                    min={60}
                    max={240}
                    step={15}
                    value={minutes}
                    onChange={(e) => setMinutes(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                  <div className="mt-1 flex justify-between text-xs text-foreground/50">
                    <span>1 hour</span>
                    <span>4 hours</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="mb-3 font-funnel text-lg font-semibold text-primary">Start time</h2>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 text-sm text-foreground/60">
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking availability
                  </div>
                ) : (
                  <select
                    value={startIso}
                    onChange={(e) => setStartIso(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground focus:border-primary focus:outline-none"
                  >
                    <option value="">Choose a start time</option>
                    {slots
                      .filter((s) => !s.unavailable)
                      .map((s) => (
                        <option key={s.start.toISOString()} value={s.start.toISOString()}>
                          {s.label}
                        </option>
                      ))}
                  </select>
                )}
                <p className="mt-3 text-sm text-foreground/60">
                  Only available times are shown, in 15 minute steps. No advance notice is needed.
                  Reservations must end by 10 PM.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="font-funnel text-lg font-semibold text-primary">Your details</h2>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number (optional)"
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-foreground/40 focus:border-primary focus:outline-none"
                />
              </section>

              {selectedStart && (
                <div className="rounded-xl bg-muted/50 p-5">
                  <p className="text-sm text-foreground/60">You are reserving</p>
                  <p className="mt-1 font-funnel text-lg font-semibold text-primary">
                    {londonDateLabel(selectedStart)}, {londonTimeLabel(selectedStart)} -{" "}
                    {londonTimeLabel(new Date(selectedStart.getTime() + minutes * 60000))}
                  </p>
                  <p className="mt-1 text-sm text-foreground/70">
                    {durationLabel(minutes)} &middot; {priceForMinutes(minutes)}
                  </p>
                </div>
              )}

              <button
                type="button"
                disabled={!canContinue || submitting}
                onClick={startReservation}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Continue to payment
              </button>
            </motion.div>
          )}

          {step === "pay" && clientSecret && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-border bg-card p-6 tablet:p-8"
            >
              {selectedStart && (
                <div className="mb-6 rounded-xl bg-muted/50 p-5">
                  <p className="font-funnel text-lg font-semibold text-primary">
                    {londonDateLabel(selectedStart)}, {londonTimeLabel(selectedStart)} -{" "}
                    {londonTimeLabel(new Date(selectedStart.getTime() + minutes * 60000))}
                  </p>
                  <p className="mt-1 text-sm text-foreground/70">
                    {WESTFIELD_POD_LABEL} &middot; {durationLabel(minutes)} &middot;{" "}
                    {priceForMinutes(minutes)}
                  </p>
                </div>
              )}

              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: { theme: "stripe" } }}
              >
                <ReservationCheckoutForm
                  amountLabel={priceForMinutes(minutes)}
                  onPaid={finishReservation}
                />
              </Elements>

              {submitting && (
                <p className="mt-4 flex items-center justify-center gap-2 text-sm text-foreground/60">
                  <Loader2 className="h-4 w-4 animate-spin" /> Confirming your reservation
                </p>
              )}
            </motion.div>
          )}

          {step === "done" && confirmation && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-border bg-card p-8 text-center"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-funnel text-3xl font-semibold text-primary">
                Your pod is reserved
              </h1>
              <p className="mt-3 text-foreground/70">
                {confirmation.date_label}, {confirmation.from_label} - {confirmation.to_label}
              </p>
              <p className="mt-1 text-sm text-foreground/60">
                {WESTFIELD_POD_LABEL} &middot; {confirmation.duration_label} &middot;{" "}
                {confirmation.amount_label} paid
              </p>

              <div className="mt-7 rounded-xl bg-muted/50 p-6">
                {confirmation.access_code ? (
                  <>
                    <p className="text-sm uppercase tracking-wide text-foreground/50">
                      Your access code
                    </p>
                    <p className="mt-2 font-funnel text-4xl font-semibold tracking-[0.15em] text-primary">
                      {confirmation.access_code}
                    </p>
                    <p className="mt-3 text-sm text-foreground/60">
                      Please use the pod only during your reserved time.
                    </p>
                  </>
                ) : (
                  <p className="text-foreground/70">
                    Check your confirmation email for your access code and directions to the pod.
                  </p>
                )}
              </div>

              <p className="mt-6 text-sm text-foreground/60">
                A confirmation email is on its way. Need to change anything?
              </p>
              <a
                href="https://wa.me/447347474059"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 font-medium text-white transition-opacity hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" />
                Message us on WhatsApp
              </a>
            </motion.div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReservePod;
